// /forgot-password — request a password-reset link. The backend always returns
// 202 (no account enumeration), so we always show the same neutral confirmation.
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "./auth-layout";
import { Alert, Button, Input } from "@/components/ui";
import { forgotPassword } from "@/api/auth";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t("Reset your password")}
      subtitle={t("We'll email you a link to choose a new password.")}
      footer={
        <Link to="/login" className="font-semibold text-brand-600 hover:underline">
          {t("Back to sign in")}
        </Link>
      }
    >
      {sent ? (
        <Alert tone="info">
          {t(
            "If an account exists for that email, a reset link is on its way. Check your inbox (and spam).",
          )}
        </Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Input
            label={t("Email")}
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <Button type="submit" size="lg" loading={submitting} className="w-full">
            {t("Send reset link")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
